import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useToast } from '@/hooks/useToast';

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'number' | 'date' | 'select' | 'textarea' | 'file' | 'checkbox' | 'radio' | 'multiselect';
  placeholder?: string;
  required?: boolean;
  options?: { value: any; label: string }[];
  validation?: z.ZodType<any>;
  description?: string;
  disabled?: boolean;
  multiple?: boolean;
  accept?: string; // for file inputs
  rows?: number; // for textarea
  min?: number; // for number inputs
  max?: number; // for number inputs
  step?: number; // for number inputs
  currentFileUrl?: string; // for showing existing files
  onDownloadFile?: () => void; // callback for custom file download
}

export interface FormSection {
  title: string;
  description?: string;
  fields: FormField[];
}

export interface MasterDataFormProps {
  title: string;
  description?: string;
  sections: FormSection[];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
  initialData?: any;
  mode: 'create' | 'edit';
  serverErrors?: Record<string, string>; // Add server errors prop
  entityName?: string; // Optional entity name for button text
}

export function MasterDataForm({
  title,
  description,
  sections,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialData,
  mode,
  serverErrors = {}, // Default to empty object
  entityName, // Optional entity name for button text
}: MasterDataFormProps) {
  // Create dynamic schema based on form fields
  const createSchema = () => {
    const schemaFields: Record<string, z.ZodType<any>> = {};
    
    sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.validation) {
          schemaFields[field.name] = field.validation;
        } else {
          // Default validation based on field type
          let fieldSchema: z.ZodType<any>;
          
          switch (field.type) {
            case 'email':
              // For optional email fields, allow empty strings
              if (field.required) {
                fieldSchema = z.string().min(1, `${field.label} is required`).email('Please enter a valid email address');
              } else {
                fieldSchema = z.preprocess(
                  (val) => (val === '' || val === null || val === undefined ? undefined : val),
                  z.string().email('Please enter a valid email address').optional()
                );
              }
              break;
            case 'number': {
              // For number fields, handle string to number conversion properly
              // Build the inner number schema with min/max constraints first
              let innerNumberSchema: z.ZodNumber | z.ZodOptional<z.ZodNumber>;

              // Start with base number schema
              let baseNumberSchema = z.number({
                required_error: `${field.label} is required`,
                invalid_type_error: `${field.label} must be a number`,
              });

              if (field.min !== undefined) {
                baseNumberSchema = baseNumberSchema.min(field.min, `${field.label} must be at least ${field.min}`);
              }
              if (field.max !== undefined) {
                baseNumberSchema = baseNumberSchema.max(field.max, `${field.label} must be at most ${field.max}`);
              }

              // For optional fields, make the inner schema optional
              innerNumberSchema = field.required ? baseNumberSchema : baseNumberSchema.optional();

              // Then wrap it with preprocess for string to number conversion
              fieldSchema = z.preprocess(
                (val) => {
                  // If empty string, null, or undefined
                  if (val === '' || val === null || val === undefined) {
                    // For required fields, return undefined so the required_error is triggered
                    // For optional fields, return undefined which is acceptable
                    return undefined;
                  }
                  // Otherwise try to convert to number
                  const num = Number(val);
                  // Return undefined if not a valid number (will trigger validation error)
                  return isNaN(num) ? undefined : num;
                },
                innerNumberSchema
              );
              break;
            }
            case 'date':
              fieldSchema = z.string();
              break;
            case 'checkbox':
              fieldSchema = z.boolean();
              break;
            case 'radio':
              fieldSchema = z.union([z.string(), z.number(), z.boolean()]);
              break;
            case 'multiselect':
              // Handle arrays that might contain objects with _id
              fieldSchema = z.preprocess(
                (val) => {
                  if (!Array.isArray(val)) return [];
                  return val.map((item: any) => {
                    if (typeof item === 'object' && item !== null && item._id) {
                      return item._id;
                    }
                    return item;
                  });
                },
                z.array(z.string())
              );
              break;
            case 'file':
              // For file inputs, accept both File objects and strings (URLs)
              fieldSchema = z.union([
                z.instanceof(File),
                z.string(),
                z.undefined()
              ]);
              break;
            case 'select':
              // For select fields, handle both string IDs and objects with _id
              fieldSchema = z.preprocess(
                (val) => {
                  if (val === '' || val === null || val === undefined) {
                    return undefined;
                  }
                  // If it's an object with _id, extract the _id
                  if (typeof val === 'object' && val !== null && '_id' in val) {
                    return (val as any)._id;
                  }
                  return val;
                },
                z.string().optional()
              );
              break;
            default:
              fieldSchema = z.string();
          }
          
          if (field.required) {
            if (field.type === 'number') {
              // For required number fields, the validation is already handled in the number case above
              // No additional modification needed here
            } else if (field.type === 'checkbox') {
              // For checkboxes, we don't typically make them required, but if needed, keep as is
            } else if (field.type === 'radio') {
              // For radio buttons, we don't use min() method
            } else if (field.type === 'multiselect') {
              // For multiselect, require at least one selection
              fieldSchema = z.array(z.string()).min(1, `Please select at least one ${field.label}`);
            } else if (field.type === 'file') {
              // For file inputs, require either a File object or a non-empty string
              // If there's a current file URL, the field can be optional
              if (field.currentFileUrl) {
                fieldSchema = z.union([
                  z.instanceof(File),
                  z.string(),
                  z.undefined()
                ]).optional();
              } else {
                fieldSchema = z.union([
                  z.instanceof(File),
                  z.string().min(1, `${field.label} is required`)
                ]);
              }
            } else if (field.type === 'select') {
              // For required select fields, ensure a value is selected
              fieldSchema = z.preprocess(
                (val) => {
                  if (val === '' || val === null || val === undefined) {
                    return undefined;
                  }
                  if (typeof val === 'object' && val !== null && '_id' in val) {
                    return (val as any)._id;
                  }
                  return val;
                },
                z.string().min(1, `${field.label} is required`)
              );
            } else if (field.type === 'email') {
              // Email validation is already handled in the case above
            } else {
              // For strings and other types that support min()
              fieldSchema = (fieldSchema as z.ZodString).min(1, `${field.label} is required`);
            }
          } else {
            // For optional fields, the select case already handles it
            if (field.type !== 'select' && field.type !== 'email') {
              fieldSchema = fieldSchema.optional();
            }
          }
          
          schemaFields[field.name] = fieldSchema;
        }
      });
    });
    
    // Use passthrough to allow extra fields like _id, createdAt, updatedAt
    return z.object(schemaFields).passthrough();
  };

  const schema = createSchema();

  const { toast } = useToast();

  const fieldNameSet = React.useMemo(() => {
    return new Set(
      sections.flatMap(section => section.fields.map(field => field.name))
    );
  }, [sections]);

  const lastServerErrorSignatureRef = React.useRef<string>('');

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: initialData || {},
    mode: 'onSubmit', // Validate on submit
  });

  React.useEffect(() => {
    if (initialData) {
      form.reset(initialData);
    } else {
      form.reset({});
    }
  }, [initialData, form]);

  React.useEffect(() => {
    if (!isOpen) return;

    const keys = Object.keys(serverErrors || {});
    if (keys.length === 0) {
      lastServerErrorSignatureRef.current = '';
      return;
    }

    const signature = keys
      .sort()
      .map(k => `${k}:${serverErrors[k]}`)
      .join('|');

    if (signature === lastServerErrorSignatureRef.current) return;
    lastServerErrorSignatureRef.current = signature;

    toast({
      variant: 'destructive',
      title: `${entityName || 'Record'} not saved`,
      description: 'Please review the highlighted fields (jumped to the first error).',
    });

    const focusField = keys.find(k => fieldNameSet.has(k));
    if (focusField) {
      form.setFocus(focusField as any);
    }
  }, [serverErrors, isOpen, toast, form, entityName, fieldNameSet]);

  const handleSubmit = (data: any) => {
    onSubmit(data);
  };

  const isSubmitDisabled =
    isLoading ||
    // In edit mode, avoid letting users submit without changes (reduces accidental no-op saves)
    (mode === 'edit' && !form.formState.isDirty);

  const onError = (errors: any) => {
    // Validation errors are shown inline per-field, but a toast + focus improves discoverability.
    const firstFieldName = errors ? Object.keys(errors)[0] : undefined;

    toast({
      variant: 'destructive',
      title: 'Fix form errors',
      description: firstFieldName
        ? 'Please review the highlighted fields (jumped to the first error).'
        : 'Please review the highlighted fields.',
    });

    if (firstFieldName) {
      // react-hook-form: focus the first invalid field to reduce “why didn’t it save?” confusion.
      form.setFocus(firstFieldName as any);
    }
  };

  const renderField = (field: FormField) => {
    const error = form.formState.errors[field.name];
    const serverError = serverErrors[field.name]; // Get server error for this field
    
    switch (field.type) {
      case 'select':
        return (
          <div key={field.name} className="space-y-2 w-full">
            <Label htmlFor={field.name} className="text-sm font-medium block">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Controller
              name={field.name}
              control={form.control}
              render={({ field: controllerField }) => (
                <Select
                  value={controllerField.value ? String(controllerField.value) : ''}
                  onValueChange={controllerField.onChange}
                  disabled={field.disabled || isLoading}
                >
                  <SelectTrigger className="w-full h-10">
                    <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.filter((option) => option.value != null && option.value !== '').map((option) => (
                      <SelectItem key={String(option.value)} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive mt-1">{error.message as string}</p>
            )}
            {serverError && (
              <p className="text-xs text-destructive mt-1">{serverError}</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} className="space-y-2 md:col-span-2 w-full">
            <Label htmlFor={field.name} className="text-sm font-medium block">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Textarea
              {...form.register(field.name)}
              placeholder={field.placeholder}
              disabled={field.disabled || isLoading}
              rows={field.rows || 3}
              className="w-full resize-none min-h-[80px]"
            />
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive mt-1">{error.message as string}</p>
            )}
            {serverError && (
              <p className="text-xs text-destructive mt-1">{serverError}</p>
            )}
          </div>
        );

      case 'file':
        return (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={field.name} className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            
            {/* Show current file if exists */}
            {field.currentFileUrl && (
              <div className="mb-2 p-3 bg-muted/50 rounded-md border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-muted-foreground">Current file uploaded</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {field.onDownloadFile ? (
                      <button
                        type="button"
                        onClick={field.onDownloadFile}
                        className="text-sm text-primary hover:underline flex items-center space-x-1 cursor-pointer bg-transparent border-none p-0"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Download File</span>
                      </button>
                    ) : (
                      <a
                        href={field.currentFileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center space-x-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        <span>View File</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <Controller
              name={field.name}
              control={form.control}
              render={({ field: controllerField }) => (
                <Input
                  type="file"
                  accept={field.accept}
                  multiple={field.multiple}
                  disabled={field.disabled || isLoading}
                  className="w-full"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) {
                      // For single file inputs, pass the File object directly
                      // For multiple file inputs, pass the FileList
                      const value = field.multiple ? Array.from(files) : files[0];
                      controllerField.onChange(value);
                    } else {
                      controllerField.onChange(undefined);
                    }
                  }}
                />
              )}
            />
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive mt-1">{error.message as string}</p>
            )}
            {serverError && (
              <p className="text-xs text-destructive mt-1">{serverError}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.name} className="space-y-2">
            <div className="flex items-start space-x-3">
              <Controller
                name={field.name}
                control={form.control}
                render={({ field: controllerField }) => (
                  <input
                    type="checkbox"
                    id={field.name}
                    checked={controllerField.value || false}
                    onChange={(e) => controllerField.onChange(e.target.checked)}
                    disabled={field.disabled || isLoading}
                    className="mt-0.5 rounded border-input focus:ring-2 focus:ring-ring text-primary"
                  />
                )}
              />
              <div className="flex-1">
                <Label htmlFor={field.name} className="text-sm font-medium cursor-pointer">
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </Label>
                {field.description && (
                  <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                )}
              </div>
            </div>
            {error && (
              <p className="text-xs text-destructive mt-1">{error.message as string}</p>
            )}
            {serverError && (
              <p className="text-xs text-destructive mt-1">{serverError}</p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={field.name} className="space-y-3">
            <Label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Controller
              name={field.name}
              control={form.control}
              render={({ field: controllerField }) => (
                <div className="flex flex-wrap gap-4">
                  {field.options?.map((option) => (
                    <div key={option.value} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id={`${field.name}-${option.value}`}
                        value={option.value}
                        checked={controllerField.value === option.value}
                        onChange={() => controllerField.onChange(option.value)}
                        disabled={field.disabled || isLoading}
                        className="rounded-full border-input focus:ring-2 focus:ring-ring text-primary"
                      />
                      <Label htmlFor={`${field.name}-${option.value}`} className="text-sm cursor-pointer">
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            />
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive mt-1">{error.message as string}</p>
            )}
            {serverError && (
              <p className="text-xs text-destructive mt-1">{serverError}</p>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <div key={field.name} className="space-y-3 md:col-span-2">
            <Label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Controller
              name={field.name}
              control={form.control}
              render={({ field: controllerField }) => {
                const selectedValues = Array.isArray(controllerField.value) ? controllerField.value : [];
                
                const handleToggle = (optionValue: string) => {
                  const newValues = selectedValues.includes(optionValue)
                    ? selectedValues.filter((v: string) => v !== optionValue)
                    : [...selectedValues, optionValue];
                  controllerField.onChange(newValues);
                };

                return (
                  <div className="border rounded-md p-3 max-h-48 overflow-y-auto bg-background">
                    {field.options && field.options.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {field.options.map((option) => (
                          <div key={option.value} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`${field.name}-${option.value}`}
                              checked={selectedValues.includes(String(option.value))}
                              onChange={() => handleToggle(String(option.value))}
                              disabled={field.disabled || isLoading}
                              className="rounded border-input focus:ring-2 focus:ring-ring text-primary"
                            />
                            <Label 
                              htmlFor={`${field.name}-${option.value}`} 
                              className="text-sm cursor-pointer truncate"
                              title={option.label}
                            >
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No options available</p>
                    )}
                  </div>
                );
              }}
            />
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {Array.isArray(form.getValues(field.name)) && form.getValues(field.name).length > 0 
                ? `${form.getValues(field.name).length} item(s) selected` 
                : 'None selected'}
            </p>
            {error && (
              <p className="text-xs text-destructive mt-1">{error.message as string}</p>
            )}
            {serverError && (
              <p className="text-xs text-destructive mt-1">{serverError}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={field.name} className="space-y-2 w-full">
            <Label htmlFor={field.name} className="text-sm font-medium block">
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Input
              {...form.register(field.name)}
              type={field.type === 'email' || field.type === 'tel' ? 'text' : field.type}
              placeholder={field.placeholder}
              disabled={field.disabled || isLoading}
              min={field.min}
              max={field.max}
              step={field.step}
              className="w-full h-10"
            />
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
            {error && (
              <p className="text-xs text-destructive mt-1">{error.message as string}</p>
            )}
            {serverError && (
              <p className="text-xs text-destructive mt-1">{serverError}</p>
            )}
          </div>
        );
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Prevent closing the dialog while a save is in-flight (clicking backdrop / pressing Escape)
        if (!open && !isLoading) onClose();
      }}
    >
      <DialogContent
        className="w-full max-w-4xl max-h-[95vh] mx-2 sm:mx-4 overflow-hidden flex flex-col"
        onEscapeKeyDown={(e) => {
          if (isLoading) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isLoading) e.preventDefault();
        }}
      >
        <DialogHeader className="flex-shrink-0 px-1 sm:px-0">
          <DialogTitle className="text-lg sm:text-xl font-semibold pr-8">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-sm text-muted-foreground">{description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 sm:pr-2">
          <form onSubmit={form.handleSubmit(handleSubmit, onError)} className="space-y-4 sm:space-y-6">
            {sections.map((section, sectionIndex) => (
              <Card key={sectionIndex} className="border shadow-sm">
                <CardHeader className="pb-3 sm:pb-4 px-3 sm:px-6">
                  <CardTitle className="text-base sm:text-lg font-medium">{section.title}</CardTitle>
                  {section.description && (
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">{section.description}</p>
                  )}
                </CardHeader>
                <CardContent className="pt-0 px-3 sm:px-6 pb-4 sm:pb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {section.fields.map(renderField)}
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t sticky bottom-0 bg-background">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                className="w-full sm:w-auto h-10 sm:h-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitDisabled}
                className="w-full sm:w-auto h-10 sm:h-auto"
                title={mode === 'edit' && !form.formState.isDirty ? 'Make a change to enable saving' : undefined}
              >
                {isLoading && <LoadingSpinner size="sm" className="mr-2" />}
                <span className="truncate">
                  {mode === 'create' ? `Create ${entityName || 'Record'}` : `Update ${entityName || 'Record'}`}
                </span>
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}